import { useEffect, useState } from "react";
import { api, formatApiError, formatTZS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Upload, X, Clock, Users } from "lucide-react";

const EMPTY = { title: "", description: "", image: "", price: 0, price_at_reception: false, includes: [], duration: "", capacity: "", order: 0, is_active: true };

export default function HotelServicesPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/services"); setRows(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: rows.length + 1 }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r, includes: r.includes || [] }); setOpen(true); };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("folder", "services");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}` }));
      toast.success("Photo uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        price: form.price_at_reception ? 0 : Number(form.price || 0),
        order: Number(form.order),
        includes: (form.includes || []).filter(Boolean),
      };
      if (editing) await api.put(`/admin/services/${editing.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success(editing ? "Service updated" : "Service added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this service?")) return;
    try { await api.delete(`/admin/services/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl text-[color:var(--deep)]">Hotel Services &amp; Facilities</h1>
          <p className="text-sm text-[color:var(--muted-text)] mt-1">Bookable facilities: meeting halls, private dinners, transfers. Guests see and book these on the landing page.</p>
        </div>
        <Button onClick={openNew} data-testid="new-service-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]"><Plus className="w-4 h-4 mr-1" /> Add service</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rows.map((s) => (
          <Card key={s.id} className={`overflow-hidden hover-lift ${s.is_active ? "" : "opacity-60"}`} data-testid={`service-card-${s.id}`}>
            <div className="h-40 bg-[color:var(--sand)] relative">
              {s.image && <img src={s.image} alt={s.title} className="w-full h-full object-cover" />}
              {!s.is_active && <Badge className="absolute top-3 right-3 bg-slate-500 text-white">Hidden</Badge>}
              {s.price_at_reception && <Badge className="absolute top-3 left-3 bg-amber-500 text-white">Price at reception</Badge>}
            </div>
            <CardContent className="p-5">
              <p className="font-heading text-lg text-[color:var(--deep)]">{s.title}</p>
              <p className="text-xs text-[color:var(--muted-text)] mt-1 line-clamp-2">{s.description}</p>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="font-semibold text-[color:var(--brand)]">{s.price_at_reception ? "At reception" : (s.price > 0 ? formatTZS(s.price) : "Free")}</span>
                {s.duration && <span className="text-xs text-[color:var(--muted-text)] flex items-center gap-1"><Clock className="w-3 h-3" /> {s.duration}</span>}
              </div>
              {s.capacity && <p className="text-xs text-[color:var(--muted-text)] mt-1 flex items-center gap-1"><Users className="w-3 h-3" /> {s.capacity}</p>}
              {(s.includes || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {s.includes.slice(0, 5).map((t, i) => (<span key={i} className="text-[11px] font-ui text-[color:var(--deep)] bg-[color:var(--sand)] border border-[color:var(--brand)]/15 px-2 py-0.5 rounded-full">{t}</span>))}
                </div>
              )}
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="destructive" onClick={() => remove(s.id)} data-testid={`del-service-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="service-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit service" : "Add service"}</DialogTitle><DialogDescription>Shown to guests on the &quot;Facilities&quot; section and available to book.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Meeting Hall (Full Day)" data-testid="svc-title" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="svc-desc" /></div>
            <div className="rounded-xl border border-[color:var(--brand)]/15 p-3 bg-[color:var(--sand)]/40">
              <div className="flex items-center gap-2">
                <Switch checked={form.price_at_reception} onCheckedChange={(v) => setForm({ ...form, price_at_reception: v })} data-testid="svc-price-reception" />
                <Label className="mb-0">Price given at reception</Label>
              </div>
              <p className="text-xs text-[color:var(--muted-text)] mt-1">Turn on to hide the price on the public page and show "Ask at reception" instead.</p>
              {!form.price_at_reception && (
                <div className="mt-3"><Label>Price (TZS)</Label><Input type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="svc-price" className="mt-1" /></div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Duration</Label><Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="e.g. Full day (8h)" data-testid="svc-duration" /></div>
              <div><Label>Capacity</Label><Input value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="e.g. Up to 40 guests" data-testid="svc-capacity" /></div>
              <div><Label>Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="svc-order" /></div>
              <div className="flex items-end gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="svc-active" /><Label>Active (visible to guests)</Label></div>
            </div>
            <div>
              <Label>What's included</Label>
              <Input
                value={(form.includes || []).join(", ")}
                onChange={(e) => setForm({ ...form, includes: e.target.value.split(",").map((v) => v.trim()).filter(Boolean) })}
                placeholder="Drinks, Refreshments, Lunch, Projector"
                className="mt-1"
                data-testid="svc-includes"
              />
              <p className="text-xs text-[color:var(--muted-text)] mt-1">Comma-separated — shown as chips on the guest page.</p>
              {(form.includes || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {form.includes.map((t, i) => <span key={i} className="text-xs font-ui text-[color:var(--brand)] bg-[color:var(--brand)]/10 px-2 py-0.5 rounded-full">{t}</span>)}
                </div>
              )}
            </div>
            <div>
              <Label>Photo</Label>
              <div className="mt-2 flex items-center gap-3">
                {form.image ? (
                  <div className="relative w-32 h-24 rounded-lg overflow-hidden">
                    <img src={form.image} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setForm({ ...form, image: "" })} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ) : null}
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[color:var(--brand)]/30 hover:bg-[color:var(--sand)] text-sm">
                  <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : (form.image ? "Replace" : "Upload")}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="svc-upload" />
                </label>
              </div>
              <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="Or paste image URL" className="mt-2" data-testid="svc-image-url" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-service-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">{editing ? "Save" : "Add service"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
